import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Add as CreateIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Analytics as AnalyticsIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import Screenshot from './Screenshot';
const QuickStartGuide = () => {

  const steps = [
    {
      label: 'Sign Up & Login',
      icon: <AccountIcon />,
      description: 'Create your SurveySlack account and access the dashboard',
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            If you haven't already, create your free SurveySlack account. You'll get access to:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Unlimited surveys on free plan" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Up to 100 responses per survey" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Basic analytics and reporting" />
            </ListItem>
          </List>
          <Button 
            variant="outlined" 
            href="/app/dashboard"
            sx={{ mt: 2 }}
          >
            Go to Dashboard
          </Button>
        </Box>
      )
    },
    {
      label: 'Create Your First Survey',
      icon: <CreateIcon />,
      description: 'Choose from templates, AI assistance, or start from scratch',
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            From your dashboard, you have three ways to create a survey:
          </Typography>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                🤖 Create with AI (Recommended)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tell our AI what you want to measure, and it will generate relevant questions for you.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                📋 Use Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose from pre-built templates for common use cases like customer satisfaction, employee feedback, etc.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                ✏️ Start from Scratch
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build your survey from the ground up with our drag-and-drop editor.
              </Typography>
            </CardContent>
          </Card>
          <Screenshot
            src="/images/screenshots/create-survey-options.png"
            alt="Create Survey Options"
            caption="Choose how to create your survey - AI, templates, or from scratch"
          />
        </Box>
      )
    },
    {
      label: 'Design Your Survey',
      icon: <EditIcon />,
      description: 'Add questions, customize themes, and set up logic',
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Once you're in the survey editor, you can:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Add different question types" 
                secondary="Multiple choice, text input, rating scales, and more"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Customize the look and feel" 
                secondary="Choose colors, fonts, and themes that match your brand"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Set up conditional logic" 
                secondary="Show/hide questions based on previous answers"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Preview your survey" 
                secondary="Test how it looks on desktop and mobile"
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Keep your surveys short and focused. Aim for 5-10 questions maximum for better completion rates.
            </Typography>
          </Alert>
          <Screenshot
            src="/images/screenshots/survey-editor.png"
            alt="Survey Editor Interface"
            caption="The survey editor with drag-and-drop functionality and live preview"
          />
        </Box>
      )
    },
    {
      label: 'Publish & Share',
      icon: <ShareIcon />,
      description: 'Make your survey live and distribute it to your audience',
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            When you're ready to collect responses, you can share your survey in multiple ways:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Direct Link" 
                secondary="Copy and paste the survey URL anywhere"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Email Invitations" 
                secondary="Send personalized email invites to specific recipients"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Embed Code" 
                secondary="Add the survey directly to your website or blog"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="QR Code" 
                secondary="Generate a QR code for easy mobile access"
              />
            </ListItem>
          </List>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Your survey is automatically mobile-responsive and will work perfectly on any device!
            </Typography>
          </Alert>
          <Screenshot
            src="/images/screenshots/share-survey.png"
            alt="Share Survey Options"
            caption="Multiple ways to share your survey - link, email, embed, or QR code"
          />
        </Box>
      )
    },
    {
      label: 'Analyze Results',
      icon: <AnalyticsIcon />,
      description: 'View real-time responses and generate insights',
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            As responses come in, you can:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="View real-time dashboard" 
                secondary="See response counts, completion rates, and trends"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Analyze individual responses" 
                secondary="Dive deep into specific answers and patterns"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Export your data" 
                secondary="Download results as CSV or Excel files"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Generate reports" 
                secondary="Create visual charts and summaries"
              />
            </ListItem>
          </List>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
        Quick Start Guide
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
        Get up and running with SurveySlack in just a few minutes. Follow these simple steps to create 
        and launch your first survey.
      </Typography>

      {/* Time Estimate */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PlayIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Estimated Time: 10-15 minutes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              From signup to collecting your first responses
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip label="Beginner Friendly" color="success" variant="outlined" />
          </Box>
        </CardContent>
      </Card>

      {/* Step-by-step Guide */}
      <Stepper orientation="vertical" sx={{ mt: 4 }}>
        {steps.map((step, index) => (
          <Step key={index} active={true}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  {step.icon}
                </Box>
              )}
            >
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {step.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepLabel>
            <StepContent>
              <Paper sx={{ p: 3, mt: 2, backgroundColor: 'grey.50' }}>
                {step.content}
              </Paper>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Next Steps */}
      <Box sx={{ mt: 6, p: 4, backgroundColor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          What's Next?
        </Typography>
        <Typography variant="body1" paragraph>
          Now that you know the basics, explore these advanced features:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Button 
            variant="outlined" 
            href="/app/documentations/creating-surveys/question-types"
          >
            Learn About Question Types
          </Button>
          <Button 
            variant="outlined"
            href="/app/documentations/publishing/share-options"
          >
            Advanced Sharing Options
          </Button>
          <Button 
            variant="outlined"
            href="/app/documentations/analytics/response-analytics"
          >
            Deep Dive into Analytics
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default QuickStartGuide;